import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Box, Paper, TextField, Button, Typography, Table, TableHead, TableBody,
  TableRow, TableCell, Chip, IconButton, Tooltip, Alert, ToggleButton,
  ToggleButtonGroup, Select, MenuItem, FormControl, InputLabel
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import { useAlert } from "react-alert";
import { FormsApi } from "../formsUtils";
import { errorMessage } from "../../../services/http";

const STATUS = {
  pending: { label: "Pendente", color: "default" },
  sent: { label: "Enviado", color: "info" },
  opened: { label: "Aberto", color: "warning" },
  completed: { label: "Respondido", color: "success" },
  expired: { label: "Expirado", color: "error" },
};

const DELIVERY = {
  queued: "enfileirado", sent: "enviado", delivered: "entregue",
  read: "lido", failed: "falhou", undelivered: "não entregue",
};

/** Geração e gestão de convites (e-mail ou WhatsApp) para os pacientes. */
export default function SharePanel({ formId, published }) {
  const alert = useAlert();
  const history = useHistory();
  const [rows, setRows] = useState([]);
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [channel, setChannel] = useState("email");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [publicLink, setPublicLink] = useState("");

  function load() {
    FormsApi.listInvitations(formId, "?page=0&limit=100").then(
      (resp) => setRows(resp?.data?.data || []),
      (error) => console.log(error)
    );
  }

  useEffect(() => {
    if (!published) return;
    load();
    FormsApi.listTemplates().then(
      (resp) => setTemplates((resp?.data?.data || []).filter((t) => t.active)),
      () => {}
    );
    FormsApi.get(formId).then(
      (resp) => {
        // A API devolve o link já carimbado, o mesmo dos convites, para o endereço mudar a
        // cada versão e não servir cópia velha guardada no navegador.
        const link = resp?.data?.public_link;
        const tk = resp?.data?.public_token;
        if (link) setPublicLink(link);
        else if (tk) {
          // Montado aqui, o link sai sem carimbo — justamente o que causa o formulário
          // antigo. Acontece quando falta FORMS_PUBLIC_BASE_URL na API, e sem o aviso o
          // operador copia o link torto sem ninguém notar a configuração ausente.
          console.warn("API não devolveu public_link; link montado sem carimbo de versão");
          setPublicLink(`${window.location.origin}/f/${tk}`);
        }
      },
      () => {}
    );
    // eslint-disable-next-line
  }, [formId, published]);

  function handleCreate() {
    if (!contact.name && !contact.email && !contact.phone) {
      alert.error("Informe ao menos nome, e-mail ou telefone");
      return;
    }
    FormsApi.createInvitations(formId, {
      contact_name: contact.name, contact_email: contact.email,
      contact_phone: contact.phone, channel,
    }).then(
      () => { setContact({ name: "", email: "", phone: "" }); load(); alert.success("Convite gerado!"); },
      (error) => alert.error(errorMessage(error, "Erro ao gerar convite"))
    );
  }

  // O convite guarda o canal com que foi criado; o toggle acima é só do próximo a criar.
  const rowChannel = (row) => row.channel || channel;

  function handleSend(row) {
    const payload = { channel: rowChannel(row) };
    if (payload.channel === "whatsapp") {
      if (!templateId) { alert.error("Selecione um template de WhatsApp"); return; }
      payload.template_id = templateId;
    }
    FormsApi.sendInvitation(formId, row.id, payload).then(
      () => { alert.success("Convite enviado!"); load(); },
      (error) => alert.error(errorMessage(error, "Erro ao enviar"), { timeout: 15000 })
    );
  }

  function handleDelete(row) {
    if (!window.confirm("Remover este convite?")) return;
    FormsApi.deleteInvitation(formId, row.id).then(() => load());
  }

  function copyLink(link) {
    navigator.clipboard.writeText(link).then(() => alert.info("Link copiado!"));
  }

  if (!published) {
    return <Alert severity="warning">Publique o formulário para gerar convites aos pacientes.</Alert>;
  }

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 3, background: "#f5f8ff" }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Link público para responder</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Qualquer pessoa com este link pode responder (resposta anônima). Ideal para divulgar.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField size="small" fullWidth value={publicLink} InputProps={{ readOnly: true }} />
          <Tooltip title="Copiar"><span>
            <IconButton onClick={() => copyLink(publicLink)} disabled={!publicLink}><ContentCopyIcon /></IconButton>
          </span></Tooltip>
          <Tooltip title="Abrir"><span>
            <IconButton onClick={() => window.open(publicLink, "_blank", "noopener")} disabled={!publicLink}><SendIcon /></IconButton>
          </span></Tooltip>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle2">Gerar e enviar convite</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ToggleButtonGroup size="small" exclusive value={channel}
              onChange={(_, v) => v && setChannel(v)}>
              <ToggleButton value="email">E-mail</ToggleButton>
              <ToggleButton value="whatsapp">WhatsApp</ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Configurar WhatsApp / templates">
              <IconButton size="small" onClick={() => history.push("/forms-whatsapp")}><SettingsIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Box>
        </Box>

        {channel === "whatsapp" && (
          <Box sx={{ mb: 1 }}>
            {templates.length === 0 ? (
              <Alert severity="info">
                Nenhum template de WhatsApp cadastrado.{" "}
                <Button size="small" onClick={() => history.push("/forms-whatsapp")}>Cadastrar template</Button>
              </Alert>
            ) : (
              <FormControl size="small" sx={{ minWidth: 280 }}>
                <InputLabel>Template WhatsApp</InputLabel>
                <Select label="Template WhatsApp" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                  {templates.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
            )}
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <TextField size="small" label="Nome" value={contact.name}
            onChange={(e) => setContact({ ...contact, name: e.target.value })} />
          {channel === "email" && (
            <TextField size="small" label="E-mail" value={contact.email}
              onChange={(e) => setContact({ ...contact, email: e.target.value })} />
          )}
          {channel === "whatsapp" && (
            <TextField size="small" label="Telefone (5511...)" value={contact.phone}
              onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
          )}
          <Button variant="contained" onClick={handleCreate}>Gerar link</Button>
        </Box>
      </Paper>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Contato</TableCell>
            <TableCell>Canal</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Entrega</TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const st = STATUS[row.status] || STATUS.pending;
            return (
              <TableRow key={row.id}>
                <TableCell>
                  <div>{row.contact_name || "-"}</div>
                  <Typography variant="caption" color="text.secondary">{row.contact_email || row.contact_phone}</Typography>
                </TableCell>
                <TableCell>{row.channel || "-"}</TableCell>
                <TableCell><Chip size="small" label={st.label} color={st.color} /></TableCell>
                <TableCell>
                  {row.delivery_status ? (
                    <Typography variant="caption">{DELIVERY[row.delivery_status] || row.delivery_status}</Typography>
                  ) : "-"}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Copiar link">
                    <IconButton size="small" onClick={() => copyLink(row.link)}><ContentCopyIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title={`Enviar via ${rowChannel(row) === "whatsapp" ? "WhatsApp" : "e-mail"}`}>
                    <IconButton size="small" onClick={() => handleSend(row)}><SendIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Remover">
                    <IconButton size="small" color="error" onClick={() => handleDelete(row)}><DeleteIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={5} align="center" style={{ color: "#999", padding: 24 }}>
              Nenhum convite gerado ainda.
            </TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
